import {useCallback, useState} from 'react';
import {getFileNameFromUrl} from '@/helpers/shadcn/utils';
import {getSasToken} from '@/services/sasToken';
import {Constants} from '@/utils/constants/commonConstants';

const extractFileNameFromBlobUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('/').pop() || '';
  } catch {
    const [withoutQuery] = url.split('?');
    return withoutQuery.split('/').pop() || '';
  }
};

const stripExistingSasTokenFromUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  return url.replace(/\?sv=.*$/i, '');
};

const stripNumericSuffixGroups = (value) => {
  if (!value) return '';

  let result = '';
  let index = 0;

  while (index < value.length) {
    const whitespaceStart = index;
    while (index < value.length && value[index] === ' ') {
      index += 1;
    }

    const tokenStart = index;
    if (value[tokenStart] === '(') {
      let cursor = tokenStart + 1;
      let hasDigit = false;

      while (
        cursor < value.length &&
        value[cursor] >= '0' &&
        value[cursor] <= '9'
      ) {
        hasDigit = true;
        cursor += 1;
      }

      if (hasDigit && value[cursor] === ')') {
        index = cursor + 1;
        continue;
      }
    }

    result += value.slice(whitespaceStart, tokenStart);
    if (tokenStart < value.length) {
      result += value[tokenStart];
      index = tokenStart + 1;
    }
  }

  return result.trim();
};

const normalizeVideoDownloadFileName = (rawFileName) => {
  if (!rawFileName || typeof rawFileName !== 'string') return 'video.mp4';

  const decodedFileName = (() => {
    try {
      return decodeURIComponent(rawFileName);
    } catch {
      return rawFileName;
    }
  })();

  const extensionMatch = /(\.[^.]*)$/.exec(decodedFileName);
  const extension = extensionMatch ? extensionMatch[1] : '';
  const nameWithoutExtension = extension
    ? decodedFileName.slice(0, -extension.length)
    : decodedFileName;

  const suffixPattern = /\((\d+)\)/g;
  const suffixMatches = Array.from(
    nameWithoutExtension.matchAll(suffixPattern)
  );

  if (!suffixMatches.length) {
    return decodedFileName;
  }

  const lastSuffixNumber = Number(suffixMatches[suffixMatches.length - 1][1]);
  const nextSuffix = Number.isNaN(lastSuffixNumber) ? 1 : lastSuffixNumber + 1;
  const baseName = stripNumericSuffixGroups(nameWithoutExtension);

  return `${baseName}(${nextSuffix})${extension}`;
};

const useMediaThumbnailDownload = ({downloadUrl}) => {
  const [downloadProgress, setDownloadProgress] = useState(null);

  const resolveVideoDownloadUrl = useCallback(async (baseVideoUrl) => {
    if (!baseVideoUrl) return '';
    const sanitizedVideoUrl = stripExistingSasTokenFromUrl(baseVideoUrl);

    const fileName = extractFileNameFromBlobUrl(sanitizedVideoUrl);
    if (!fileName) return sanitizedVideoUrl;

    try {
      const sasResponse = await getSasToken(fileName, Constants.ActivityMedia, {
        mediaType: 'video'
      });
      const sasToken = sasResponse?.sasToken?.replace(/^\?/, '');

      if (!sasToken) return sanitizedVideoUrl;

      return `${sanitizedVideoUrl}${sanitizedVideoUrl.includes('?') ? '&' : '?'}${sasToken}`;
    } catch {
      return sanitizedVideoUrl;
    }
  }, []);

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) return;

    const sanitizedDownloadUrl = stripExistingSasTokenFromUrl(downloadUrl);

    const fileName = normalizeVideoDownloadFileName(
      getFileNameFromUrl(sanitizedDownloadUrl)
    );

    try {
      setDownloadProgress(0);
      const resolvedDownloadUrl =
        await resolveVideoDownloadUrl(sanitizedDownloadUrl);

      if (!resolvedDownloadUrl) {
        setDownloadProgress(null);
        return;
      }

      const xhr = new XMLHttpRequest();
      xhr.open('GET', resolvedDownloadUrl, true);
      xhr.responseType = 'blob';
      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setDownloadProgress(percent);
        }
      };
      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          setDownloadProgress(100);
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          setTimeout(() => {
            setDownloadProgress(null);
          }, 800);
        } else {
          setDownloadProgress(null);
        }
      };
      xhr.onerror = () => {
        setDownloadProgress(null);
      };
      xhr.send();
    } catch {
      setDownloadProgress(null);
    }
  }, [downloadUrl, resolveVideoDownloadUrl]);

  return {
    downloadProgress,
    handleDownload
  };
};

export default useMediaThumbnailDownload;
